import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import React from 'react';
import { useIntl } from 'react-intl';

import { isDisplayableDate } from 'containers/OfferLetterTemplates/constants';
import { FieldLabel, fieldLabelStyle } from 'containers/OfferLetterTemplates/Editor/FieldLabel';
import globalMessages from 'shared/global/messages';

import { DataKeyOption, TypedInput } from './types';

/**
 * The stored answers of a boolean key's Yes/No pair; the labels beside them are
 * translated. They must equal what the template editor's condition builder
 * stores for the same pair, or a condition on a boolean key never matches.
 */
export const YES = 'true';
export const NO = 'false';

export type TypedControl = 'date' | 'yes_no' | 'combobox' | 'text';

/**
 * Which control a data key renders as (HIR-375 change 2): the key's own type
 * decides date and boolean, and the opening's options decide a combobox.
 *
 * A value the control cannot display falls to a control that can: a date input
 * clears a malformed date and a radio pair shows nothing for a third answer, so
 * either would put a blank control over a field that still holds — and would
 * submit — a value. The combobox and the text box show anything.
 */
export const typedControl = (input: TypedInput, current: string): TypedControl => {
  const hasOptions = (input.options ?? []).length > 0;

  if (input.key_type === 'date' && (current === '' || isDisplayableDate(current))) return 'date';
  if (input.key_type === 'boolean') {
    return current === '' || current === YES || current === NO ? 'yes_no' : 'combobox';
  }
  return hasOptions ? 'combobox' : 'text';
};

const useStyles = makeStyles(theme => ({
  // The radio pair keeps a real <legend>, styled as every other label here.
  legend: fieldLabelStyle(theme),
  clear: {
    marginLeft: theme.spacing(1),
  },
}));

export interface TypedFieldProps {
  /** The control's anchor, as `revealFirstMissing` looks it up. */
  id: string;
  label: string;
  value: string;
  input: TypedInput;
  error?: boolean;
  /** Below the control: validation and other feedback. */
  helperText?: string;
  disabled?: boolean;
  /**
   * Whether the recruiter may empty the answer. A condition-only key may; a
   * field that merges into the document still requires a value. Only the
   * Yes/No pair needs an affordance for it — every other control empties by
   * deleting its text.
   */
  clearable?: boolean;
  /**
   * Whether the answer prints in the document. The letter prints the stored
   * value as written (`MergeValueResolver#format_value`), so a field that
   * merges shows each option by its value, not its label: what the recruiter
   * sees is what the letter says. A condition input shows labels, because its
   * answer only decides which blocks render.
   */
  merges?: boolean;
  onChange: (value: string) => void;
}

/** A typed answer that matches an option's label stores the option's value. */
const valueForInput = (options: DataKeyOption[], typed: string): string =>
  options.find(option => option.label === typed)?.value ?? typed;

const labelForValue = (options: DataKeyOption[], value: string): string =>
  options.find(option => option.value === value)?.label ?? value;

/**
 * One data key's input on the Send Offer form: a combobox over the opening's
 * options that also takes a typed value, a date picker, a Yes/No pair, or free
 * text.
 */
export const TypedField: React.FC<TypedFieldProps> = ({
  id,
  label,
  value,
  input,
  error = false,
  helperText,
  disabled = false,
  clearable = false,
  merges = false,
  onChange,
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const control = typedControl(input, value);
  const helperTextId = helperText ? `${id}-helper-text` : undefined;
  const yesNoOptions: DataKeyOption[] = [
    { value: YES, label: merges ? YES : intl.formatMessage(globalMessages.yes) },
    { value: NO, label: merges ? NO : intl.formatMessage(globalMessages.no) },
  ];

  if (control === 'yes_no') {
    const labelId = `${id}-label`;
    return (
      <Box mt={2} mb={1}>
        <FormControl component="fieldset" error={error} fullWidth id={id} disabled={disabled}>
          <FormLabel component="legend" id={labelId} className={classes.legend} focused={false}>
            {label}
          </FormLabel>
          <Box display="flex" alignItems="center">
            <RadioGroup
              row
              aria-labelledby={labelId}
              aria-describedby={helperTextId}
              name={id}
              value={value}
              onChange={event => onChange(event.target.value)}
            >
              {yesNoOptions.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {clearable && value !== '' && (
              <Button
                size="small"
                variant="text"
                className={classes.clear}
                disabled={disabled}
                onClick={() => onChange('')}
              >
                {intl.formatMessage(globalMessages.clear)}
              </Button>
            )}
          </Box>
          {helperText && <FormHelperText id={helperTextId}>{helperText}</FormHelperText>}
        </FormControl>
      </Box>
    );
  }

  if (control === 'combobox') {
    const authored = input.key_type === 'boolean' ? yesNoOptions : input.options ?? [];
    const options = merges ? authored.map(option => ({ ...option, label: option.value })) : authored;
    const typed = labelForValue(options, value).trim().toLowerCase();
    return (
      <Box mt={2} mb={1}>
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        <Autocomplete<string>
          id={id}
          freeSolo
          // freeSolo hides the arrow by default; the list is still there to open.
          forcePopupIcon
          fullWidth
          disabled={disabled}
          options={options.map(option => option.value)}
          getOptionLabel={option => labelForValue(options, option)}
          // Every keystroke becomes `value`, so the box always echoes the
          // selection and MUI's own filter never narrows the list. A value
          // that names an option shows the whole list; anything else filters it.
          filterOptions={values => {
            if (typed === '' || options.some(option => option.value === value)) return values;
            return values.filter(option => labelForValue(options, option).toLowerCase().includes(typed));
          }}
          value={value}
          inputValue={labelForValue(options, value)}
          onChange={(_event, next) => onChange(next ?? '')}
          onInputChange={(_event, typed, reason) => {
            // `reset` is the component echoing a selection back into the box;
            // the selection itself arrives through onChange.
            if (reason !== 'reset') onChange(valueForInput(options, typed));
          }}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              error={error}
              helperText={helperText}
            />
          )}
        />
      </Box>
    );
  }

  return (
    <Box mt={2} mb={1}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <TextField
        id={id}
        fullWidth
        variant="outlined"
        type={control === 'date' ? 'date' : 'text'}
        value={value}
        onChange={event => onChange(event.target.value)}
        disabled={disabled}
        error={error}
        helperText={helperText}
      />
    </Box>
  );
};
